import React from 'react';
import PropTypes from 'prop-types';

import _ from 'lodash';
import { withRouter } from 'next/router';

import {
  DESTINATION_FIELD,
  CHECK_IN_FIELD,
  CHECK_OUT_FIELD,
  ROOM_PARTY,
} from '../utils/fieldsNames';

import {
  filterKeyNames,
  filtersWithArrays,
  decodeRoomsFormUrl,
  convertStrToInt,
} from '../utils/routingHelpers';

export const getUrlSearchParams = router => {
  const { query } = router;
  const queryCopy = Object.assign({}, query);

  // remove empty keys
  const filteredQueryCopy = _.omit(queryCopy, ['']);

  const getSearchOnly = obj => ({
    [DESTINATION_FIELD]: obj[DESTINATION_FIELD],
    [CHECK_IN_FIELD]: obj[CHECK_IN_FIELD],
    [CHECK_OUT_FIELD]: obj[CHECK_OUT_FIELD],
    [ROOM_PARTY]: obj[ROOM_PARTY],
  });

  let reduceToSearchOnly = convertStrToInt(getSearchOnly(filteredQueryCopy));

  // _.pickBy appears to convert value back to string
  const activeFilters = getActiveFilters(router);

  let result = {};
  result.hotelSearchCriteria = { ...reduceToSearchOnly };

  const rooms = result.hotelSearchCriteria[ROOM_PARTY];

  result.hotelSearchCriteria[ROOM_PARTY] = rooms && decodeRoomsFormUrl(rooms);

  if (!_.isEmpty(activeFilters)) {
    result.HotelFiltersInput = { ...activeFilters };
  }

  result.sort = filteredQueryCopy.sort;
  result.offset = convertStrToInt(filteredQueryCopy).offset;
  result.limit = convertStrToInt(filteredQueryCopy).limit;
  result.hotelId = filteredQueryCopy.hotelId;

  return result;
};

export const getActiveFilters = router => {
  // pick only the filters from router query params
  let filters = convertStrToInt(
    _.pickBy(Object.assign({}, router.query), (val, key) => {
      return filterKeyNames.indexOf(key) !== -1;
    })
  );

  // make sure that filters with arrays are only added into an array before they are used
  _.forOwn(filters, (value, key) => {
    if (filtersWithArrays.indexOf(key) !== -1 && !Array.isArray(value)) {
      let array = [];
      array.push(value);
      filters[key] = array;
    }
  });

  return filters;
};

export default Component => {
  const DecoratedComponent = props => {
    const { children, router, ...rest } = props;

    const processNewFilters = (
      filters,
      activeFilters = getActiveFilters(router)
    ) => {
      const newFilters = Object.assign({}, activeFilters);

      _.forOwn(filters, (filter, type) => {
        if (Array.isArray(filter)) {
          if (filter.length) {
            // add new filter selection
            newFilters[type] = filter;
          } else {
            // when the array is empty, remove the filter
            delete newFilters[type];
          }
        } else {
          if (filter) {
            newFilters[type] = filter;
          } else {
            delete newFilters[type];
          }
        }
      });

      return newFilters;
    };

    const updateFilters = filters => {
      updateParamsWithFilters(processNewFilters(filters));
    };

    const updateParamsWithFilters = newFilters => {
      const { query } = router;
      const newQuery = Object.assign({}, query);
      // remove existing filters and replace with the new ones
      filterKeyNames.forEach(filterKey => {
        if (newQuery[filterKey]) {
          delete newQuery[filterKey];
        }
      });

      return updateUrlParams({ ...newQuery, ...newFilters });
    };

    const updateUrlParams = newParams => {
      const { pathname, push } = router;

      const path = {
        pathname,
        query: newParams,
      };

      push(path, undefined, { shallow: true });
    };

    const removeFilters = () => {
      const newQuery = Object.assign({}, router.query);
      Object.keys(newQuery).forEach(paramKey => {
        if (filterKeyNames.indexOf(paramKey) !== -1) {
          delete newQuery[paramKey];
        }
      });
      updateUrlParams(newQuery);
    };

    return (
      <Component
        router={router}
        activeFilters={getActiveFilters(router)}
        searchParams={getUrlSearchParams(router)}
        updateFilters={updateFilters}
        updateParamsWithFilters={updateParamsWithFilters}
        processNewFilters={processNewFilters}
        removeFilters={removeFilters}
        updateUrlParams={updateUrlParams}
        {...rest}
      >
        {children}
      </Component>
    );
  };

  DecoratedComponent.propTypes = {
    router: PropTypes.shape({
      query: PropTypes.object,
      pathname: PropTypes.string.isRequired,
      push: PropTypes.func.isRequired,
    }),
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  };

  return withRouter(DecoratedComponent);
};
